// composed-contains.ts — PSTUDIO-1433

/**
 * `Node.contains()` across open shadow boundaries.
 *
 * The panel's JSON editor is a web component: everything the user actually
 * clicks — `.cm-content`, the gutter, the scroller — lives inside
 * `ps-code-editor`'s shadow root, and the pointerdown handler records
 * `composedPath()[0]`, i.e. that shadow-internal node. Native `contains()` is
 * scoped to one node tree, so `panel.contains(shadowInternalNode)` is `false`
 * even though the click plainly happened inside the panel — and the blur guard
 * then read it as a click on the grid and saved, which is the exact behaviour
 * the ticket forbids ("clicking within or around the panel should not trigger a
 * save").
 *
 * Walking parents and hopping host-ward at each shadow root answers the
 * question the guard is really asking: is this node inside the panel as the
 * user sees it.
 */
export function composedContains(
  container: Node | null | undefined,
  node: Node | null | undefined
): boolean {
  if (!container || !node) return false;
  let current: Node | null = node;
  // A malformed/clobbered tree must not hang the main thread: `<form>` and
  // `document` expose named-property getters, so an element named "host" can
  // make a naive walk jump back DOWN and loop forever. The DOM is never this
  // deep in practice, so a ceiling costs nothing and removes the hazard.
  let hops = 0;
  while (current && hops++ < MAX_HOPS) {
    if (current === container) return true;
    // Step host-ward ONLY at a real ShadowRoot. Duck-typing `host` here was
    // wrong twice over: `HTMLAnchorElement.host` / `HTMLAreaElement.host` are
    // STRINGS ("example.com:443"), so a click on a link inside the panel walked
    // into a string, ended the loop and reported "outside" — i.e. it saved,
    // which is what the guard exists to prevent; and a clobbered `host` could
    // redirect the walk into the subtree.
    const parent: Node | null = isShadowRoot(current)
      ? current.host
      : current.parentNode;
    current = parent;
  }
  return false;
}

const MAX_HOPS = 1000;

/**
 * A real ShadowRoot, without relying on the `ShadowRoot` global (absent in the
 * node test environment): only a ShadowRoot has both a `host` that is itself a
 * node and no `parentNode`.
 */
function isShadowRoot(node: Node): node is ShadowRoot {
  const { host } = node as { host?: unknown };
  return (
    // A ShadowRoot IS a DocumentFragment (nodeType 11). Checking
    // `parentNode === null` instead was too loose: `document` also has no
    // parent (nodeType 9) AND exposes a named-property getter, so a page
    // containing an element named "host" made the walk jump DOWN into that
    // element's subtree — and if it sat inside the panel, a click on the GRID
    // would have been read as "inside the panel" and the commit suppressed.
    node.nodeType === DOCUMENT_FRAGMENT_NODE &&
    !!host &&
    typeof host === 'object' &&
    'nodeType' in (host as object)
  );
}

/** `Node.DOCUMENT_FRAGMENT_NODE`, spelled out — the constant is not available
 * in the node test environment, where `Node` is undefined. */
const DOCUMENT_FRAGMENT_NODE = 11;
